import React, { useEffect, useContext } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { setThemeVariables, isInternationalTheme } from "config/ConfigUtils";

import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";

import Analysis from "features/operations/AnalysisManagement";

import Live from "pages/TrackVehiclesPage";

import Shifts from "pages/Shifts";
import Stops from "pages/Stops";

import UsersPage from "pages/UsersPage";
import ServicesPage from "pages/ServicesPage";

import Dashboard from "pages/Dashboard";

import Settings from "pages/SettingsPage";

import Accounting from "pages/AccountingPage";
import Inventory from "pages/InventoryPage";

import WalkingDistance from "features/walking-distance/WalkingDistancePage";

// Driver Scheduler — see: backend/docs/driver-scheduler.md
// Tüm UI features/driver-scheduler/ altında izole.
import DriverSchedulerDaily from "features/driver-scheduler/DailySchedule";

import CustomerRequestInbox from "features/customer-portal/CustomerRequestInbox";
import CustomerRequestNew from "features/customer-portal/CustomerRequestNew";
import PortalOperatorGuard from "features/customer-portal/PortalOperatorGuard";

import TransfersPage from "pages/TransfersPage";

// Telemetri Uyarı Merkezi — see: backend/docs/arvento-telemetry.md §20
import Admin from "pages/AdminPage";

import CheckAccess from "components/shared/CheckAccess";

// Health Checks — yalnızca admin/impersonation (sayfa içinde guard'lı)
import HealthChecks from "pages/HealthChecksPage";

import Schools from "pages/SchoolsPage";

// import VehicleRoutes from "pages/VehicleRoutes";
// import NewVehicleRouteItem from "deprecated/vehicle-routes/NewVehicleRouteItem";

import Login from "pages/auth/Login";
import ForgotPassword from "pages/auth/ForgotPassword";
import RecoverPassword from "pages/auth/RecoverPassword";
import AcceptInvite from "pages/auth/AcceptInvite";
import SignUp from "pages/auth/SignUp";
import VerifyEmail from "pages/auth/VerifyEmail";
import Demo from "pages/auth/Demo";
import StartRoute from "pages/start/StartRoute";
import DefaultRedirect from "pages/start/DefaultRedirect";
import { DEMO_ROUTE_PATH } from "features/start/start-flags";
import Terms from "./pages/auth/Terms";
import Privacy from "./pages/auth/Privacy";
import SmsOptIn from "./pages/auth/SmsOptIn";
import MixedSmsOptIn from "./pages/auth/MixedSmsOptIn";
import NotFound from "pages/auth/NotFound";
import LandingPage from "pages/landing/LandingPage";
import MainNavigation from "navigation/MainNavigation";
import { AuthContext } from "shared/context/auth-context";
import { useAuth } from "shared/hooks/auth-hook";

import { StoreContext } from "shared/context/store-context";
import { useStore } from "shared/hooks/store-hook";
import { useHttpClient } from "shared/hooks/http-hook";

import ShiftManagement from "pages/ShiftManagement";
import { ThemeProvider } from "@mui/material/styles";

import { PERMISSION_TYPES, isCustomerContactRole } from "@routebot/constants";
import { storeUtmParams } from "shared/util/utm";
import { applyStoredOpenAIUser } from "shared/util/openai-ads";
import AiChatProvider from "features/ai-chat/AiChatContext";
import DemoLeadCaptureProvider from "components/demo/DemoLeadCaptureProvider";

const DemoWrapper = ({ isDemo, children }) => {
  if (isDemo) {
    return <DemoLeadCaptureProvider>{children}</DemoLeadCaptureProvider>;
  }
  return children;
};

// AppContent - AuthContext'in İÇİNDE render edilir, böylece useStore auth'a erişebilir
const AppContent = () => {
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();

  const {
    isAdmin,
    users,
    userMap,
    studentToParentMap,
    parentToStudentMap,
    drivers,
    driverOptions,
    vehicleOptions,
    companies,
    relations,
    companyOptions,
    schools,
    studentSchools,
    personelSchools,
    studentSchoolOptions,
    personelSchoolOptions,
    vehicles,
    schoolOptions,
    stopOptions,
    studentOptions,
    parentOptions,
    personelOptions,
    vehicleRoutes,
    vehicleRouteOptions,
    bundledRoutes,
    students,
    storeReady,
    contracts,
    educationYears,
    discounts,
    serviceTemplates,
    educationYearsOptions,
    selectedEducationYearId,
    discountOptions,
    serviceTemplateOptions,
    receipts,
    isProcessing,
    withdraws,
    contractsOptions,
    expenses,
    vehiclePayments,
    transfers,
    transferregions,
    transferregionOptions,
    shifts,
    stops,
    userOptions,
    shiftOptions,
    shiftUsers,
    shiftUserOptions,
    // Personel Services
    standartPersonelServices,
    standartPersonelServiceOptions,
    shiftPersonelServices,
    shiftPersonelServiceOptions,
    InsertUser,
    UpdateUser,
    DeleteUser,
    InsertStudent,
    UpdateStudent,
    DeleteStudent,
    InsertRelation,
    UpdateRelation,
    DeleteRelation,
    InsertShift,
    UpdateShift,
    DeleteShift,
    UpdateShifts,
    InsertStop,
    UpdateStop,
    DeleteStop,
    InsertVehicleRoute,
    UpdateVehicleRoute,
    DeleteVehicleRoute,
    InsertBundledRoute,
    UpdateBundledRoute,
    DeleteBundledRoute,
    UpdateBundledRoutes,
    UpdateRelations,
    UpdateVehicles,
    UpdateCompanies,
    UpdateVehicleRoutes,
    UpdateSchools,
    InsertSchool,
    UpdateSchool,
    DeleteSchool,
    UpdateUsers,
    UpdateStudents,
    UpdateEverything,
    RetrieveContracts,
    InsertContract,
    UpdateContract,
    DeleteContract,
    RetrieveReceipts,
    RetrieveWithdraws,
    RetrieveExpenses,
    RetrieveAccountingUtils,
    UpdateVehiclePayments,
    UpdateTransfers,
    UpdateTransferregions,
    UpdatePersonelServices,
    InsertPersonelService,
    UpdatePersonelService,
    DeletePersonelService,
    GetParentFromStudent,
    UpdateDrivers,
    InsertDriver,
    UpdateDriver,
    DeleteDriver,
    UpdateSelectedEducationYearId,
    RetrieveEducationYears,
    automations,
    automationOptions,
    InsertAutomation,
    UpdateAutomation,
    DeleteAutomation,
    RetrieveAutomations,
    InsertTransfer,
    UpdateTransfer,
    DeleteTransfer,
    InsertReceipt,
    UpdateReceipt,
    DeleteReceipt,
    InsertWithdraw,
    UpdateWithdraw,
    DeleteWithdraw,
    InsertVehiclePayment,
    UpdateVehiclePayment,
    DeleteVehiclePayment,
    InsertServiceTemplate,
    UpdateServiceTemplate,
    DeleteServiceTemplate,
    InsertDiscount,
    UpdateDiscount,
    DeleteDiscount,
    InsertExpense,
    UpdateExpense,
    DeleteExpense,
    InsertEducationYear,
    UpdateEducationYear,
    DeleteEducationYear,
    InsertTransferRegion,
    UpdateTransferRegion,
    DeleteTransferRegion,
    InsertVehicle,
    UpdateVehicle,
    DeleteVehicle,

    driverDetails,
    InsertDriverDetail,
    UpdateDriverDetail,
    DeleteDriverDetail,

    attendants,
    attendantOptions,
    InsertAttendant,
    UpdateAttendant,
    DeleteAttendant,
    custodies,
    InsertCustody,
    UpdateCustody,
    DeleteCustody,

    accidents,
    maintenances,
    thirdParties,
    InsertAccident,
    UpdateAccident,
    DeleteAccident,
    RetrieveMaintenances,
    InsertMaintenance,
    UpdateMaintenance,
    DeleteMaintenance,
    RetrieveThirdParties,
    InsertThirdParty,
    UpdateThirdParty,
    DeleteThirdParty,

    fuels,
    InsertFuel,
    UpdateFuel,
    DeleteFuel,

    stocks,
    stockOptions,
    RetrieveStocks,
    InsertStock,
    UpdateStock,
    DeleteStock,

    stockMovements,
    RetrieveStockMovements,
    InsertStockMovement,
    UpdateStockMovement,
    DeleteStockMovement,
  } = useStore();

  // Auth context'ten gerekli değerleri al
  const { token, id: clientId, permissions: clientPermissions, loaded: clientLoaded, logout, theme } = auth;

  useEffect(() => {
    setThemeVariables();
    storeUtmParams();
    applyStoredOpenAIUser();
  }, []);

  useEffect(() => {
    const UpdateStore = async () => {
      if (Boolean(clientId) && token) {
        try {
          await UpdateEverything();
        } catch (err) {
          if (err.code === 401) {
            logout();
          }
        }
      }
    };

    UpdateStore();
  }, [UpdateEverything, sendRequest, logout, clientId, token]);

  let routes;

  if (clientLoaded && !token) {
    routes = (
      <Routes>
        <Route index element={<Navigate to={isInternationalTheme() ? "/start" : "/login"} replace />} />
        <Route path="/start" element={<StartRoute />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password" element={<ForgotPassword />} />
        <Route path="/recover-password" element={<RecoverPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/sms-opt-in" element={<SmsOptIn />} />
        <Route path="/mixed-sms-opt-in" element={<MixedSmsOptIn />} />
        <Route path="/not-found" element={<NotFound />} />

        {isInternationalTheme() && <Route path="/landing" element={<LandingPage />} />}
        {isInternationalTheme() && <Route path="/sign-up" element={<SignUp />} />}
        {isInternationalTheme() && <Route path="/verify-email" element={<VerifyEmail />} />}
        {isInternationalTheme() && <Route path="/demo" element={<Demo />} />}

        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    );
  }

  if (clientLoaded && token) {
    routes = (
      <Routes>
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/start" element={<StartRoute />} />
        {isInternationalTheme() && <Route path="/demo" element={<Navigate to={DEMO_ROUTE_PATH} replace />} />}
        {isInternationalTheme() && <Route path="/sign-up" element={<Navigate to="/dashboard" replace />} />}
        <Route element={<CheckAccess />}>
          <Route path="/" element={<DefaultRedirect />} />
          <Route path="/login" element={<DefaultRedirect />} />

          {/* <Route path="/plans" element={<Plans />} end /> */}
          <Route path="/settings" element={<Settings />} end />

          <Route path="/dashboard" element={<Dashboard />} end />

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_INVENTORY} />}>
            <Route path="/inventory" element={<Inventory />} end />
          </Route>

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_USERS} />}>
            <Route path="/users" element={<UsersPage />} end />
          </Route>

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_TRANSFERS} />}>
            <Route path="/transfers" element={<TransfersPage />} end />
            {/* <Route path="/transfers" element={<Transfer />} end />
            <Route path="/new/transferregion" element={<TransferRegionNew />} end />
            <Route path="/new/transfer" element={<TransferNew />} /> */}
          </Route>

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_LIVE} />}>
            <Route path="/live" element={<Live />} end />
          </Route>

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_ACCOUNTING} />}>
            <Route path="/accounting" element={<Accounting />} end />
          </Route>

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_SERVICE_REPORTS} />}>
            <Route path="/analysis" element={<Analysis />} end />
          </Route>

          <Route
            element={
              <CheckAccess access={[PERMISSION_TYPES.ACCESS_TO_PERSONELS, PERMISSION_TYPES.ACCESS_TO_STUDENTS]} />
            }
          >
            <Route path="/services" element={<ServicesPage />} end />
          </Route>

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_SHIFTS} />}>
            <Route path="/shifts" element={<Shifts />} end />
          </Route>

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_STOPS} />}>
            <Route path="/stops" element={<Stops />} end />
          </Route>

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_SHIFTS} />}>
            <Route path="/shiftmanagement" element={<ShiftManagement />} end />
          </Route>

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_ADMIN} />}>
            <Route path="/admin" element={<Admin />} end />
          </Route>

          {/* Health Checks — erişim sayfa içinde (super_admin veya admin impersonation) kontrol edilir */}
          <Route path="/health-checks" element={<HealthChecks />} end />

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_WALKING_DISTANCE} />}>
            <Route path="/walking-distance" element={<WalkingDistance />} end />
          </Route>

          {/* Driver Scheduler — Sprint 2: günlük Gantt */}
          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_PERSONELS} />}>
            <Route path="/driver-scheduler/daily" element={<DriverSchedulerDaily />} end />
          </Route>

          <Route
            path="/customer-requests/:id?"
            element={
              <PortalOperatorGuard>
                <CustomerRequestInbox variant="operator" />
              </PortalOperatorGuard>
            }
          />

          <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_SCHOOLS} />}>
            <Route path="/schools" element={<Schools />} end />
          </Route>

          <Route path="/inventory" element={<Inventory />} end />

          <Route path="/reset-password" element={<ForgotPassword />} end />
          <Route path="/recover-password" element={<RecoverPassword />} end />

          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/sms-opt-in" element={<SmsOptIn />} />
          <Route path="/mixed-sms-opt-in" element={<MixedSmsOptIn />} />

          <Route path="/not-found" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/not-found" />} />
        </Route>
      </Routes>
    );
  }

  if (clientLoaded && token && clientPermissions?.[PERMISSION_TYPES.ACCESS_TO_TRACK_VEHICLE_ONLY]) {
    routes = (
      <Routes>
        <Route element={<CheckAccess access={PERMISSION_TYPES.ACCESS_TO_TRACK_VEHICLE_ONLY} />}>
          <Route path="/live" element={<Live />} end />
        </Route>
        <Route path="*" element={<Navigate to="/live" />} />
      </Routes>
    );
  }

  if (clientLoaded && token && isCustomerContactRole(auth.client?.role)) {
    routes = (
      <Routes>
        <Route path="/portal/requests/new" element={<CustomerRequestNew />} end />
        <Route path="/portal/requests/:id?" element={<CustomerRequestInbox variant="contact" />} />
        <Route path="/settings" element={<Settings />} end />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="*" element={<Navigate to="/portal/requests" />} />
      </Routes>
    );
  }

  return (
    <StoreContext.Provider
      value={{
        isAdmin: isAdmin,
        users: users,
        userMap: userMap,
        studentToParentMap: studentToParentMap,
        parentToStudentMap: parentToStudentMap,
        drivers: drivers,
        driverOptions: driverOptions,
        companies: companies,
        companyOptions: companyOptions,
        studentOptions: studentOptions,

        parentOptions: parentOptions,
        personelOptions: personelOptions,
        schools: schools,
        studentSchools: studentSchools,
        personelSchools: personelSchools,
        studentSchoolOptions: studentSchoolOptions,
        personelSchoolOptions: personelSchoolOptions,
        relations: relations,
        vehicles: vehicles,
        vehicleOptions: vehicleOptions,
        students: students,
        schoolOptions: schoolOptions,
        stopOptions: stopOptions,
        vehicleRoutes: vehicleRoutes,
        bundledRoutes: bundledRoutes,
        storeReady: storeReady,
        contracts: contracts,
        educationYears: educationYears,
        discounts: discounts,
        serviceTemplates: serviceTemplates,
        educationYearsOptions: educationYearsOptions,
        selectedEducationYearId: selectedEducationYearId,
        discountOptions: discountOptions,
        serviceTemplateOptions: serviceTemplateOptions,
        receipts: receipts,
        withdraws: withdraws,
        isProcessing: isProcessing,
        contractsOptions: contractsOptions,
        expenses: expenses,
        vehicleRouteOptions: vehicleRouteOptions,
        vehiclePayments: vehiclePayments,
        transfers: transfers,
        transferregions: transferregions,
        transferregionOptions: transferregionOptions,
        shifts: shifts,
        stops: stops,
        userOptions: userOptions,
        shiftOptions: shiftOptions,
        shiftUsers: shiftUsers,
        shiftUserOptions: shiftUserOptions,

        // Personel Services
        standartPersonelServices: standartPersonelServices,
        standartPersonelServiceOptions: standartPersonelServiceOptions,
        shiftPersonelServices: shiftPersonelServices,
        shiftPersonelServiceOptions: shiftPersonelServiceOptions,

        InsertUser: InsertUser,
        UpdateUser: UpdateUser,
        DeleteUser: DeleteUser,

        InsertStudent: InsertStudent,
        UpdateStudent: UpdateStudent,
        DeleteStudent: DeleteStudent,

        InsertRelation: InsertRelation,
        UpdateRelation: UpdateRelation,
        DeleteRelation: DeleteRelation,

        InsertShift: InsertShift,
        UpdateShift: UpdateShift,
        DeleteShift: DeleteShift,

        InsertSchool: InsertSchool,
        UpdateSchool: UpdateSchool,
        DeleteSchool: DeleteSchool,

        UpdateShifts: UpdateShifts,

        InsertStop: InsertStop,
        UpdateStop: UpdateStop,
        DeleteStop: DeleteStop,

        InsertVehicleRoute: InsertVehicleRoute,
        UpdateVehicleRoute: UpdateVehicleRoute,
        DeleteVehicleRoute: DeleteVehicleRoute,

        InsertBundledRoute: InsertBundledRoute,
        UpdateBundledRoute: UpdateBundledRoute,
        DeleteBundledRoute: DeleteBundledRoute,
        UpdateBundledRoutes: UpdateBundledRoutes,

        UpdateVehicles: UpdateVehicles,

        UpdateRelations: UpdateRelations,
        UpdateVehicleRoutes: UpdateVehicleRoutes,
        UpdateCompanies: UpdateCompanies,
        UpdateSchools: UpdateSchools,
        UpdateUsers: UpdateUsers,
        UpdateStudents: UpdateStudents,
        UpdateEverything: UpdateEverything,
        RetrieveContracts: RetrieveContracts,
        InsertContract: InsertContract,
        UpdateContract: UpdateContract,
        DeleteContract: DeleteContract,

        RetrieveEducationYears: RetrieveEducationYears,

        RetrieveReceipts: RetrieveReceipts,
        RetrieveWithdraws: RetrieveWithdraws,
        RetrieveExpenses: RetrieveExpenses,
        RetrieveAccountingUtils: RetrieveAccountingUtils,
        UpdateVehiclePayments: UpdateVehiclePayments,
        UpdateTransfers: UpdateTransfers,
        UpdateTransferregions: UpdateTransferregions,
        UpdatePersonelServices: UpdatePersonelServices,
        InsertPersonelService: InsertPersonelService,
        UpdatePersonelService: UpdatePersonelService,
        DeletePersonelService: DeletePersonelService,
        GetParentFromStudent: GetParentFromStudent,
        UpdateDrivers: UpdateDrivers,
        InsertDriver: InsertDriver,
        UpdateDriver: UpdateDriver,
        DeleteDriver: DeleteDriver,
        UpdateSelectedEducationYearId: UpdateSelectedEducationYearId,

        automations: automations,
        automationOptions: automationOptions,
        InsertAutomation: InsertAutomation,
        UpdateAutomation: UpdateAutomation,
        DeleteAutomation: DeleteAutomation,
        RetrieveAutomations: RetrieveAutomations,

        InsertTransfer: InsertTransfer,
        UpdateTransfer: UpdateTransfer,
        DeleteTransfer: DeleteTransfer,

        InsertReceipt: InsertReceipt,
        UpdateReceipt: UpdateReceipt,
        DeleteReceipt: DeleteReceipt,

        InsertServiceTemplate: InsertServiceTemplate,
        UpdateServiceTemplate: UpdateServiceTemplate,
        DeleteServiceTemplate: DeleteServiceTemplate,

        InsertDiscount: InsertDiscount,
        UpdateDiscount: UpdateDiscount,
        DeleteDiscount: DeleteDiscount,

        InsertExpense: InsertExpense,
        UpdateExpense: UpdateExpense,
        DeleteExpense: DeleteExpense,

        InsertEducationYear: InsertEducationYear,
        UpdateEducationYear: UpdateEducationYear,
        DeleteEducationYear: DeleteEducationYear,

        InsertWithdraw: InsertWithdraw,
        UpdateWithdraw: UpdateWithdraw,
        DeleteWithdraw: DeleteWithdraw,

        InsertVehiclePayment: InsertVehiclePayment,
        UpdateVehiclePayment: UpdateVehiclePayment,
        DeleteVehiclePayment: DeleteVehiclePayment,

        InsertTransferRegion: InsertTransferRegion,
        UpdateTransferRegion: UpdateTransferRegion,
        DeleteTransferRegion: DeleteTransferRegion,

        InsertVehicle: InsertVehicle,
        UpdateVehicle: UpdateVehicle,
        DeleteVehicle: DeleteVehicle,

        driverDetails: driverDetails,
        InsertDriverDetail: InsertDriverDetail,
        UpdateDriverDetail: UpdateDriverDetail,
        DeleteDriverDetail: DeleteDriverDetail,

        attendants: attendants,
        attendantOptions: attendantOptions,
        InsertAttendant: InsertAttendant,
        UpdateAttendant: UpdateAttendant,
        DeleteAttendant: DeleteAttendant,

        custodies: custodies,
        InsertCustody: InsertCustody,
        UpdateCustody: UpdateCustody,
        DeleteCustody: DeleteCustody,

        accidents: accidents,
        InsertAccident: InsertAccident,
        UpdateAccident: UpdateAccident,
        DeleteAccident: DeleteAccident,
        maintenances: maintenances,
        RetrieveMaintenances: RetrieveMaintenances,
        InsertMaintenance: InsertMaintenance,
        UpdateMaintenance: UpdateMaintenance,
        DeleteMaintenance: DeleteMaintenance,
        thirdParties: thirdParties,
        RetrieveThirdParties: RetrieveThirdParties,
        InsertThirdParty: InsertThirdParty,
        UpdateThirdParty: UpdateThirdParty,
        DeleteThirdParty: DeleteThirdParty,

        fuels: fuels,
        InsertFuel: InsertFuel,
        UpdateFuel: UpdateFuel,
        DeleteFuel: DeleteFuel,

        stocks: stocks,
        stockOptions: stockOptions,
        RetrieveStocks: RetrieveStocks,
        InsertStock: InsertStock,
        UpdateStock: UpdateStock,
        DeleteStock: DeleteStock,

        stockMovements: stockMovements,
        RetrieveStockMovements: RetrieveStockMovements,
        InsertStockMovement: InsertStockMovement,
        UpdateStockMovement: UpdateStockMovement,
        DeleteStockMovement: DeleteStockMovement,
      }}
    >
      <ThemeProvider theme={theme}>
        <AiChatProvider>
          <ToastContainer
            position="top-right"
            autoClose={1500}
            hideProgressBar={true}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="dark"
            transition={Bounce}
            style={{ top: "70px" }}
          />
          <DemoWrapper isDemo={auth.isLoggedIn && auth.company?.isDemo}>
            <main style={{ width: "100%" }}>
              <MainNavigation />
              {routes}
            </main>
          </DemoWrapper>
        </AiChatProvider>
      </ThemeProvider>
    </StoreContext.Provider>
  );
};

// App - AuthContext Provider'ı sağlar
const App = () => {
  window.toast = toast;

  const {
    token,
    client: clientClient,
    id: clientId,
    tokenExpirationDate,

    name: clientName,
    email: clientEmail,
    permissions: clientPermissions,
    schools: clientSchools,
    company: clientCompany,
    timezone: clientTimezone,
    timezones: clientTimezones,
    countryCode: clientCountryCode,
    brand: clientBrand,

    loaded: clientLoaded,

    theme,

    login,
    logout,
    UpdateTheme,
  } = useAuth();

  // clientLoaded false iken loading göster
  if (!clientLoaded) {
    return <LoadingSpinner asOverlay />;
  }

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: clientLoaded && !!token,
        token: token,
        id: clientId,
        tokenExpirationDate: tokenExpirationDate,

        name: clientName,
        email: clientEmail,
        permissions: clientPermissions,
        timezone: clientTimezone,
        timezones: clientTimezones,
        countryCode: clientCountryCode,
        schools: clientSchools,
        company: clientCompany,
        brand: clientBrand,
        loaded: clientLoaded,
        theme: theme,
        client: clientClient,
        login: login,
        logout: logout,
        UpdateTheme: UpdateTheme,
      }}
    >
      <AppContent />
    </AuthContext.Provider>
  );
};

export default App;
